import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eye, ChevronRight, Check, X, RotateCcw } from "lucide-react";

const WORD_RE = /([A-Za-z']+)/g;

function normalize(s) {
  return (s || "").toLowerCase().replace(/[^a-z']/g, "");
}

function grade(target, guess) {
  const t = (target.toLowerCase().match(WORD_RE) || []);
  const g = (guess.toLowerCase().match(WORD_RE) || []);
  if (t.length === 0) return { accuracy: 1, missed: [] };
  const counts = {};
  t.forEach((w) => (counts[w] = (counts[w] || 0) + 1));
  let hits = 0;
  g.forEach((w) => { if (counts[w] > 0) { hits++; counts[w]--; } });
  const missed = Object.entries(counts).filter(([, c]) => c > 0).map(([w]) => w);
  return { accuracy: hits / t.length, missed };
}

export default function LineByLineMode({ sectionText, onScore }) {
  const lines = useMemo(
    () => sectionText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l && !/^\[[^\]]+\]$/.test(l)),
    [sectionText]
  );

  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState([]); // {accuracy, missed}
  const [finished, setFinished] = useState(false);
  const inputRef = useRef(null);
  const missedRef = useRef([]);

  useEffect(() => {
    setIdx(0); setAnswer(""); setRevealed(false);
    setResults([]); setFinished(false);
    missedRef.current = [];
  }, [sectionText]);

  useEffect(() => {
    if (!finished && !revealed) {
      // give focus back to input as user advances
      inputRef.current?.focus();
    }
  }, [idx, finished, revealed]);

  const current = lines[idx];
  const previous = idx > 0 ? lines[idx - 1] : null;

  const check = () => {
    if (!current) return;
    const r = grade(current, answer);
    setResults((rs) => [...rs, r]);
    missedRef.current = [...missedRef.current, ...r.missed];
    setRevealed(true);
  };

  const advance = () => {
    if (idx + 1 >= lines.length) {
      // Finished — average across lines, report up
      const acc = results.length
        ? results.reduce((a, r) => a + r.accuracy, 0) / results.length
        : 1;
      setFinished(true);
      onScore?.(acc, missedRef.current);
      return;
    }
    setIdx((i) => i + 1);
    setAnswer("");
    setRevealed(false);
  };

  const skip = () => {
    setResults((rs) => [...rs, { accuracy: 0, missed: current.match(WORD_RE) || [] }]);
    missedRef.current = [...missedRef.current, ...(current.match(WORD_RE) || [])];
    setRevealed(true);
  };

  const restart = () => {
    setIdx(0); setAnswer(""); setRevealed(false);
    setResults([]); setFinished(false); missedRef.current = [];
  };

  const progress = Math.round(((idx + (revealed ? 1 : 0)) / Math.max(lines.length, 1)) * 100);
  const lastResult = revealed ? results[results.length - 1] : null;

  if (finished) {
    const avg = results.length ? results.reduce((a, r) => a + r.accuracy, 0) / results.length : 0;
    const perfect = results.filter((r) => r.accuracy >= 0.999).length;
    return (
      <div data-testid="line-mode-summary" className="text-center py-6">
        <p className="overline-label text-accent">Line-by-line complete</p>
        <p className="mt-3 font-display text-6xl md:text-7xl text-primary" data-testid="line-mode-final-acc">
          {Math.round(avg * 100)}%
        </p>
        <p className="mt-2 text-muted-foreground">
          {perfect} of {results.length} lines perfect.
        </p>
        <Button onClick={restart} className="mt-8 rounded-full h-11 px-6" data-testid="line-mode-restart-btn">
          <RotateCcw size={16} className="mr-1.5" /> Run it again
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="line-mode">
      {/* Progress + previous line for context */}
      <div className="flex items-center justify-between text-sm mb-4">
        <span className="overline-label" data-testid="line-mode-counter">
          Line {idx + 1} of {lines.length}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{progress}%</span>
      </div>
      <div className="h-1 rounded-full bg-secondary overflow-hidden mb-8">
        <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      {previous && (
        <p className="text-muted-foreground italic mb-2 text-lg lyric-line" data-testid="line-mode-previous">
          {previous}
        </p>
      )}

      <p className="overline-label mb-3 text-accent">What comes next?</p>

      {!revealed ? (
        <>
          <input
            ref={inputRef}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") check(); }}
            placeholder="Sing it, then type what you remember…"
            className="w-full p-5 rounded-2xl border border-border bg-card lyric-line text-2xl md:text-3xl leading-relaxed outline-none focus:ring-2 focus:ring-primary transition-shadow"
            data-testid="line-mode-input"
            autoFocus
          />
          <div className="mt-5 flex items-center gap-3">
            <Button onClick={check} className="rounded-full h-11 px-6" data-testid="line-mode-check-btn">
              <Check size={16} className="mr-1.5" /> Check line
            </Button>
            <Button onClick={skip} variant="ghost" className="rounded-full text-muted-foreground hover:text-foreground"
                    data-testid="line-mode-reveal-btn">
              <Eye size={16} className="mr-1.5" /> Just show me
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Tip: press Enter to check.</p>
        </>
      ) : (
        <div data-testid="line-mode-reveal">
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="overline-label mb-2 text-accent">Correct line</p>
            <p className="lyric-line text-2xl md:text-3xl leading-relaxed">{current}</p>
            {lastResult && (
              <p className="mt-3 text-sm">
                <span className={`font-mono ${lastResult.accuracy >= 0.999 ? "text-primary" : lastResult.accuracy >= 0.5 ? "text-accent" : "text-destructive"}`}>
                  {Math.round(lastResult.accuracy * 100)}%
                </span>
                <span className="text-muted-foreground">
                  {lastResult.accuracy >= 0.999 ? " — locked in" : lastResult.missed.length ? ` — missed: ${lastResult.missed.slice(0, 5).join(", ")}` : ""}
                </span>
              </p>
            )}
          </div>
          <div className="mt-5 flex items-center gap-3">
            <Button onClick={advance} className="rounded-full h-11 px-6" data-testid="line-mode-next-btn">
              {idx + 1 >= lines.length ? "Finish" : "Next line"} <ChevronRight size={16} className="ml-1" />
            </Button>
            <Button
              onClick={() => { setAnswer(""); setRevealed(false); setResults((rs) => rs.slice(0, -1)); }}
              variant="ghost"
              className="rounded-full text-muted-foreground hover:text-foreground"
              data-testid="line-mode-retry-btn"
            >
              <X size={16} className="mr-1.5" /> Retry this line
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
